/**
 * Empreinte du bundle — la clé de version du hors ligne
 * (`ARCHITECTURE_V2.md` §3.2, étape 2 du §8).
 *
 * Elle sert **deux** consommateurs, et c'est pour cela qu'elle vit ici plutôt
 * que dans l'un des deux :
 *
 *   - le **cache persisté** de TanStack Query l'emploie comme `buster` : un
 *     déploiement qui change la forme d'un DTO trouverait sinon un cache de
 *     l'ancienne forme et planterait à la lecture, hors ligne, sans moyen de se
 *     réparer (`lib/persist.ts`) ;
 *   - le **service worker** la reçoit dans l'URL de son script (`/sw.js?v=…`),
 *     ce qui nomme son cache de coquille et déclenche la purge des versions
 *     antérieures à l'`activate` (`public/sw.js`).
 *
 * 🔴 **Pourquoi le nom du script d'entrée, et non une constante injectée au
 * build.** Vite hache le chunk d'entrée d'après son contenu *et* d'après le nom
 * de ses imports, qui portent eux-mêmes leur hachage : le nom
 * `assets/index-<hash>.js` change donc dès que **n'importe quoi** dans le graphe
 * de modules change. C'est exactement la propriété demandée — « le hachage du
 * bundle » — obtenue sans plugin de build à écrire et à maintenir.
 *
 * ⚠️ **Le critère est bien le code du front, pas celui du serveur.** Si un DTO
 * change de forme, le front qui le lit change avec lui, donc l'empreinte change
 * et le cache est jeté. Si le serveur change seul sans que le front bouge, le
 * cache conservé est celui que ce front-là sait lire : le garder est correct.
 *
 * ⚠️ **Une modification purement CSS ne change pas l'empreinte** — la feuille a
 * son propre hachage, distinct de celui du script. C'est voulu : rien ne
 * justifie de jeter le journal alimentaire en cache parce qu'une couleur a
 * bougé.
 */

/**
 * Reconnaît le suffixe haché posé par Vite : `…-DcBpxghN.js`.
 *
 * ⚠️ En développement, le script d'entrée est `/src/main.tsx` — il ne matche
 * pas, et c'est voulu : l'empreinte doit rester **stable** entre deux
 * rechargements à chaud, sinon chaque modification de source viderait le cache
 * persisté et rendrait le hors ligne intestable en local.
 */
const HACHAGE_VITE = /-([A-Za-z0-9_-]{6,})\.[a-z]+$/;

/**
 * Empreinte de session, tirée une fois.
 *
 * 🔴 **Repli volontairement destructeur, et c'est le point important de ce
 * fichier.** Si l'extraction échoue **en production** — Vite changeant un jour
 * sa convention de nommage —, renvoyer une constante figée serait le pire des
 * cas : le `buster` ne changerait plus jamais, un cache d'une forme périmée
 * survivrait à tous les déploiements, et **rien ne le signalerait**. On renvoie
 * donc une valeur unique par session, ce qui vide le cache persisté à chaque
 * démarrage.
 *
 * L'arbitrage est explicite : on perd le hors ligne plutôt que de servir un
 * cache incohérent. Un hors ligne qui ne marche plus se voit tout de suite ; un
 * cache d'ancienne forme se manifeste par un écran cassé qu'on ne sait pas
 * relier à sa cause.
 */
let empreinte: string | null = null;

function calculer(): string {
  const script =
    typeof document === 'undefined'
      ? null
      : document.querySelector<HTMLScriptElement>('script[type="module"][src]');
  const src = script?.getAttribute('src') ?? null;

  if (src !== null) {
    const trouve = HACHAGE_VITE.exec(src);
    if (trouve?.[1] !== undefined) return trouve[1];
  }

  // Développement : pas de hachage, et il n'en faut pas (cf. `HACHAGE_VITE`).
  if (import.meta.env.DEV) return 'dev';

  console.warn(
    "[nutri] Empreinte du bundle introuvable : le cache hors ligne est désactivé " +
      'pour cette session. Vérifier la convention de nommage de Vite.',
  );
  return `sans-empreinte-${String(Date.now())}`;
}

/** Empreinte du bundle courant. Stable pour toute la durée de la session. */
export function buildVersion(): string {
  empreinte ??= calculer();
  return empreinte;
}
